import React, { useState, useRef, useMemo } from 'react';
import { motion } from 'framer-motion';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell, ResponsiveContainer, Legend } from 'recharts';
import { ChevronRight, ChevronDown } from 'lucide-react';

const EntityAnalysis = ({ data }) => {
  const [selectedEntityType, setSelectedEntityType] = useState('bank');
  const [expandedSections, setExpandedSections] = useState(['bank']);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [popoverPosition, setPopoverPosition] = useState({ top: true, left: '50%', transform: 'translateX(-50%)' });
  const [activeEvidenceTab, setActiveEvidenceTab] = useState('positive');
  
  // Reference for entity cards
  const entityRefs = useRef({});
  
  // Process entity data with case-insensitive handling to avoid duplicates
  const processedData = useMemo(() => {
    const processedEntities = {};
    
    // Process each entity type
    Object.keys(data).forEach(type => {
      const entitiesByName = {};
      
      // Group by lowercase name to avoid duplicates
      data[type].forEach(entity => {
        const lowerName = entity.name.toLowerCase();
        
        if (!entitiesByName[lowerName]) {
          entitiesByName[lowerName] = {
            name: entity.name, // Keep original casing for display
            count: 0,
            positive: 0,
            negative: 0,
            neutral: 0
          };
        }
        
        // Aggregate counts
        entitiesByName[lowerName].count += entity.count;
        entitiesByName[lowerName].positive += entity.positive;
        entitiesByName[lowerName].negative += entity.negative;
        entitiesByName[lowerName].neutral += entity.neutral;
      });
      
      // Convert back to array and sort by count
      processedEntities[type] = Object.values(entitiesByName)
        .sort((a, b) => b.count - a.count);
    });
    
    return processedEntities;
  }, [data]);
  
  const toggleSection = (section) => {
    setExpandedSections(prev => 
      prev.includes(section)
        ? prev.filter(s => s !== section)
        : [...prev, section]
    );
  };
  
  // Get list of entity types
  const entityTypes = Object.keys(processedData).filter(type => processedData[type].length > 0);
  
  // Ensure we have a valid selected entity type
  if (entityTypes.length > 0 && !entityTypes.includes(selectedEntityType)) {
    setSelectedEntityType(entityTypes[0]);
  }
  
  // Format entity data for multi-column visualization
  const formatEntityDataForBarChart = (entities) => {
    return entities.slice(0, 8).map(entity => ({
      name: entity.name.length > 12 ? entity.name.substring(0, 12) + '...' : entity.name,
      fullName: entity.name,
      Positive: entity.positive,
      Negative: entity.negative,
      Neutral: entity.neutral,
      Total: entity.count
    }));
  };
  
  // Function to calculate popover position
  const calculatePopoverPosition = (element) => {
    if (!element) return;
    
    const rect = element.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    // Calculate available space in each direction
    const spaceAbove = rect.top;
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceLeft = rect.left;
    const spaceRight = viewportWidth - rect.right;
    
    // Determine position based on available space
    let position = {
      top: spaceBelow >= 250 || spaceBelow > spaceAbove, // Prefer below unless not enough space
      left: '50%',
      transform: 'translateX(-50%)'
    };
    
    // Adjust horizontal position if too close to edges
    if (rect.left < 300) {
      // Too close to left edge
      position.left = '0';
      position.transform = 'translateX(0)';
    } else if (viewportWidth - rect.right < 300) {
      // Too close to right edge
      position.left = 'auto';
      position.right = '0';
      position.transform = 'translateX(0)';
    }
    
    setPopoverPosition(position);
  };
  
  // Custom tooltip for bar chart
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 shadow-md rounded-md">
          <p className="text-sm font-medium">{label}</p>
          <div className="mt-1">
            {payload.map((entry, index) => (
              <div key={index} className="flex items-center text-xs mt-1">
                <div 
                  className="w-2 h-2 rounded-full mr-2" 
                  style={{ backgroundColor: entry.color }}
                ></div>
                <span>{entry.name}: {entry.value}</span>
              </div>
            ))}
          </div>
          <div className="text-xs font-medium mt-2 pt-1 border-t border-gray-200">
            Total: {payload.find(p => p.name === 'Total')?.value || 
                   payload.reduce((sum, entry) => 
                     entry.name !== 'Total' ? sum + entry.value : sum, 0)} mentions
          </div>
        </div>
      );
    }
    return null;
  };
  
  // Function to determine bar color based on sentiment ratio
  const getBarColor = (ratio) => {
    if (ratio > 0.5) return '#22c55e'; // Very positive
    if (ratio > 0) return '#86efac'; // Slightly positive
    if (ratio === 0) return '#94a3b8'; // Neutral
    if (ratio > -0.5) return '#fca5a5'; // Slightly negative
    return '#ef4444'; // Very negative
  };
  
  // Get caption for entity type
  const getEntityTypeCaption = (type) => {
    const captions = {
      bank: 'Banking institutions mentioned',
      person: 'Staff members and people mentioned',
      product: 'Banking products mentioned',
      location: 'Branch locations mentioned',
      organization: 'Organizations mentioned',
      term: 'Banking terms and concepts',
      service: 'Bank services mentioned'
    };
    
    return captions[type] || `${type.charAt(0).toUpperCase() + type.slice(1)} entities mentioned`;
  };
  
  // Mock evidence for entities (in a real app, this would come from the API)
  const getEntityEvidence = (entity) => {
    // Generate mock evidence with different sentiments
    const phrases = [
      `Customer mentioned "${entity.name}" in a positive context regarding service.`,
      `User had issues with "${entity.name}" and expressed dissatisfaction.`,
      `The "${entity.name}" was referenced in customer feedback about new features.`,
      `Positive experience reported with "${entity.name}" in account management.`,
      `Multiple complaints about "${entity.name}" response times were recorded.`,
      `"${entity.name}" was mentioned neutrally in discussions about banking options.`,
      `Customers appreciate the expertise of "${entity.name}" in resolving issues.`,
      `Some users found "${entity.name}" interface confusing and difficult to navigate.`,
      `Regular mentions of "${entity.name}" in general banking discussions.`,
      `"${entity.name}" received praise for quick problem resolution.`
    ];
    
    // Create 8-15 pieces of evidence with varying sentiments
    const count = Math.floor(Math.random() * 8) + 8;
    const evidence = [];
    
    for (let i = 0; i < count; i++) {
      // Weight the distribution according to the entity's sentiment profile
      let sentiment;
      const rand = Math.random() * entity.count;
      if (rand < entity.positive) {
        sentiment = 'positive';
      } else if (rand < entity.positive + entity.neutral) {
        sentiment = 'neutral';
      } else {
        sentiment = 'negative';
      }
      
      evidence.push({
        id: `evidence-${i}`,
        text: phrases[i % phrases.length],
        sentiment: sentiment
      });
    }
    
    return evidence;
  };
  
  return (
    <motion.div 
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
      className="bg-white p-5 rounded-xl shadow-sm border border-gray-100"
    >
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-800">Named Entity Analysis</h3>
        <p className="text-sm text-gray-500">Entities mentioned in customer feedback with sentiment analysis</p>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2">
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-sm font-medium mb-3">Entity Types</div>
            <div className="space-y-2">
              {entityTypes.map(type => (
                <div 
                  key={type}
                  className="rounded-lg border border-gray-200 overflow-hidden"
                >
                  <div 
                    className={`flex items-center justify-between p-3 cursor-pointer hover:bg-gray-100 ${selectedEntityType === type ? 'bg-indigo-50' : 'bg-white'}`}
                    onClick={() => {
                      setSelectedEntityType(type);
                      if (!expandedSections.includes(type)) {
                        toggleSection(type);
                      }
                    }}
                  >
                    <div>
                      <div className={`text-sm font-medium ${selectedEntityType === type ? 'text-indigo-700' : 'text-gray-700'}`}>
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </div>
                      <div className="text-xs text-gray-500">
                        {processedData[type].length} unique entities
                      </div>
                    </div>
                    <button 
                      className="p-1 rounded-full hover:bg-gray-200"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleSection(type);
                      }}
                    >
                      {expandedSections.includes(type) ? (
                        <ChevronDown className="w-4 h-4 text-gray-500" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-gray-500" />
                      )}
                    </button>
                  </div>
                  
                  {/* {expandedSections.includes(type) && (
                    <div className="bg-white p-3 border-t border-gray-200">
                      <div className="text-xs text-gray-500 mb-2">
                        {getEntityTypeCaption(type)}
                      </div>
                      <div className="space-y-1.5 max-h-40 overflow-y-auto">
                        {processedData[type].map((entity, index) => (
                          <div 
                            key={index} 
                            className={`flex items-center justify-between p-1.5 rounded-md text-xs ${
                              selectedEntityType === type ? 'hover:bg-indigo-50' : 'hover:bg-gray-50'
                            }`}
                          >
                            <div className="flex items-center">
                              <div 
                                className="w-2 h-2 rounded-full mr-2"
                                style={{ 
                                  backgroundColor: getBarColor(
                                    entity.count > 0
                                    ? ((entity.positive - entity.negative) / entity.count)
                                    : 0
                                  )
                                }}
                              ></div>
                              <span>{entity.name}</span>
                            </div>
                            <span className="text-gray-500">{entity.count}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )} */}
                </div>
              ))}
            </div>
          </div>
        </div>
        
        <div className="lg:col-span-3">
          {processedData[selectedEntityType] && processedData[selectedEntityType].length > 0 ? (
            <div>
              <div className="flex justify-between items-center mb-4">
                <div className="text-sm font-medium">
                  {selectedEntityType.charAt(0).toUpperCase() + selectedEntityType.slice(1)} Sentiment Analysis
                </div>
                <div className="text-xs bg-indigo-100 text-indigo-800 rounded-full px-2.5 py-0.5">
                  {processedData[selectedEntityType].length} entities
                </div>
              </div>
              
              {/* Multi-column bar chart showing sentiment breakdown */}
              <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6 shadow-sm">
                <div className="flex justify-between items-center mb-3">
                  <div className="text-sm font-medium text-gray-700">
                    Top Entities by Mention Count
                  </div>
                  <div className="flex space-x-1">
                    <div className="flex items-center text-xs">
                      <div className="w-2 h-2 rounded-full bg-blue-500 mr-1"></div>
                      <span>Positive</span>
                    </div>
                    <div className="flex items-center text-xs ml-2">
                      <div className="w-2 h-2 rounded-full bg-gray-400 mr-1"></div>
                      <span>Neutral</span>
                    </div>
                    <div className="flex items-center text-xs ml-2">
                      <div className="w-2 h-2 rounded-full bg-red-400 mr-1"></div>
                      <span>Negative</span>
                    </div>
                  </div>
                </div>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={formatEntityDataForBarChart(processedData[selectedEntityType])}
                      margin={{ top: 10, right: 10, left: 5, bottom: 20 }}
                      barGap={0}
                      barCategoryGap={8}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="name"
                        tick={{ fontSize: 11, fill: '#4b5563' }}
                        tickLine={false}
                        axisLine={{ stroke: '#e5e7eb' }}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 11, fill: '#4b5563' }}
                        tickFormatter={(value) => value.toLocaleString()}
                      />
                      <Tooltip 
                        content={<CustomTooltip />}
                        cursor={{ fill: 'rgba(224, 231, 255, 0.2)' }}
                      />
                      <Bar 
                        dataKey="Positive" 
                        stackId="sentiment" 
                        fill="#22c55e" 
                        name="Positive" 
                        radius={[3, 3, 0, 0]} 
                      />
                      <Bar 
                        dataKey="Neutral" 
                        stackId="sentiment" 
                        fill="#94a3b8" 
                        name="Neutral" 
                        radius={[0, 0, 0, 0]} 
                      />
                      <Bar 
                        dataKey="Negative" 
                        stackId="sentiment" 
                        fill="#ef4444" 
                        name="Negative" 
                        radius={[0, 0, 3, 3]} 
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="mt-2 text-xs text-gray-500 text-center">
                  Sentiment distribution for top {formatEntityDataForBarChart(processedData[selectedEntityType]).length} entities in {selectedEntityType} category
                </div>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-sm font-medium mb-2">Sentiment Breakdown</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {processedData[selectedEntityType].slice(0, 6).map((entity, index) => {
                    // Calculate sentiment percentages
                    const positivePercentage = Math.round((entity.positive / entity.count) * 100);
                    const negativePercentage = Math.round((entity.negative / entity.count) * 100);
                    const neutralPercentage = 100 - positivePercentage - negativePercentage;
                    
                    // Calculate gradient based on sentiment
                    const gradientStyle = {
                      background: `linear-gradient(135deg, 
                        rgba(34, 197, 94, ${positivePercentage / 100 * 0.2}), 
                        rgba(148, 163, 184, ${neutralPercentage / 100 * 0.15}), 
                        rgba(239, 68, 68, ${negativePercentage / 100 * 0.15}))`
                    };
                    
                    return (
                      <div 
                        key={index}
                        ref={el => entityRefs.current[entity.name] = el}
                        className={`p-2 rounded-lg shadow-sm border border-gray-200 relative cursor-pointer transition-all duration-200 hover:shadow-md ${
                          selectedEntity?.name === entity.name ? 'ring-2 ring-blue-400' : ''
                        }`}
                        style={gradientStyle}
                        onClick={(e) => {
                          const newSelected = selectedEntity?.name === entity.name ? null : {
                            ...entity, 
                            evidence: getEntityEvidence(entity)
                          };
                          setSelectedEntity(newSelected);
                          
                          if (newSelected) {
                            calculatePopoverPosition(e.currentTarget);
                            
                            // Select tab with non-zero count
                            const evidence = getEntityEvidence(entity);
                            const positiveCount = evidence.filter(e => e.sentiment === 'positive').length;
                            const negativeCount = evidence.filter(e => e.sentiment === 'negative').length;
                            const neutralCount = evidence.filter(e => e.sentiment === 'neutral').length;
                            
                            if (positiveCount > 0) {
                              setActiveEvidenceTab('positive');
                            } else if (negativeCount > 0) {
                              setActiveEvidenceTab('negative');
                            } else if (neutralCount > 0) {
                              setActiveEvidenceTab('neutral');
                            } else {
                              setActiveEvidenceTab('positive');
                            }
                          }
                        }}
                      >
                        <div className="flex justify-between items-center mb-1">
                          <div className="font-medium text-sm truncate text-slate-800" title={entity.name}>
                            {entity.name}
                          </div>
                          <div className="text-xs bg-white bg-opacity-80 text-gray-700 rounded-full px-2 py-0.5 whitespace-nowrap ml-1">
                            {entity.count}
                          </div>
                        </div>
                        <div className="flex items-center">
                          <div className="flex-grow bg-white bg-opacity-70 h-4 rounded-full overflow-hidden">
                            <div className="flex h-full">
                              <div 
                                className="bg-green-500 h-full flex items-center justify-center"
                                style={{ width: `${positivePercentage}%` }}
                              >
                                {positivePercentage >= 15 && (
                                  <span className="text-white text-xs font-bold">{positivePercentage}%</span>
                                )}
                              </div>
                              <div 
                                className="bg-gray-400 h-full flex items-center justify-center"
                                style={{ width: `${neutralPercentage}%` }}
                              >
                                {neutralPercentage >= 15 && (
                                  <span className="text-white text-xs font-bold">{neutralPercentage}%</span>
                                )}
                              </div>
                              <div 
                                className="bg-red-500 h-full flex items-center justify-center"
                                style={{ width: `${negativePercentage}%` }}
                              >
                                {negativePercentage >= 15 && (
                                  <span className="text-white text-xs font-bold">{negativePercentage}%</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="flex justify-between text-xs mt-1 text-gray-500">
                          <div className="flex items-center">
                            <div className="w-2 h-2 rounded-full bg-green-500 mr-1"></div>
                            <span>{entity.positive}</span>
                          </div>
                          <div className="flex items-center">
                            <div className="w-2 h-2 rounded-full bg-gray-400 mr-1"></div>
                            <span>{entity.neutral}</span>
                          </div>
                          <div className="flex items-center">
                            <div className="w-2 h-2 rounded-full bg-red-500 mr-1"></div>
                            <span>{entity.negative}</span>
                          </div>
                        </div>
                        
                        {/* Popover for evidence */}
                        {selectedEntity?.name === entity.name && (
                          <div 
                            className={`absolute ${popoverPosition.top ? 'top-full' : 'bottom-full'} mt-1 z-20 bg-white rounded-lg shadow-xl border border-gray-200 p-3`} 
                            style={{ 
                              width: 'min(600px, 90vw)', 
                              left: popoverPosition.left,
                              right: popoverPosition.right,
                              transform: popoverPosition.transform
                            }}
                          >
                            <div className="flex justify-between items-center mb-2">
                              <div className="font-medium">Evidence for "{entity.name}"</div>
                              <button 
                                className="text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedEntity(null);
                                }}
                              >
                                ✕
                              </button>
                            </div>
                            
                            {/* Sentiment filter tabs */}
                            <div className="flex border-b border-gray-200 mb-2">
                              <button
                                className={`py-1 px-2 text-xs font-medium rounded-t-md mr-1 ${
                                  activeEvidenceTab === 'positive'
                                    ? 'bg-green-50 border-l border-r border-t border-green-200 text-green-600'
                                    : 'text-gray-500 hover:text-gray-700 bg-transparent'
                                }`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setActiveEvidenceTab('positive');
                                }}
                              >
                                Positive ({selectedEntity.evidence.filter(p => p.sentiment === 'positive').length})
                              </button>
                              <button
                                className={`py-1 px-2 text-xs font-medium rounded-t-md mr-1 ${
                                  activeEvidenceTab === 'negative'
                                    ? 'bg-red-50 border-l border-r border-t border-red-200 text-red-600'
                                    : 'text-gray-500 hover:text-gray-700 bg-transparent'
                                }`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setActiveEvidenceTab('negative');
                                }}
                              >
                                Negative ({selectedEntity.evidence.filter(p => p.sentiment === 'negative').length})
                              </button>
                              <button
                                className={`py-1 px-2 text-xs font-medium rounded-t-md ${
                                  activeEvidenceTab === 'neutral'
                                    ? 'bg-gray-50 border-l border-r border-t border-gray-200 text-gray-600'
                                    : 'text-gray-500 hover:text-gray-700 bg-transparent'
                                }`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setActiveEvidenceTab('neutral');
                                }}
                              >
                                Neutral ({selectedEntity.evidence.filter(p => p.sentiment === 'neutral').length})
                              </button>
                            </div>
                            
                            <div className="overflow-y-auto max-h-60 pr-1" style={{ scrollbarWidth: 'thin' }}>
                              {selectedEntity.evidence
                                .filter(evidence => evidence.sentiment === activeEvidenceTab)
                                .map((evidence, i) => (
                                  <div key={i} className="py-2 px-2 border-b border-gray-100 text-sm last:border-0 hover:bg-gray-50">
                                    <div className="flex items-start gap-2">
                                      <span 
                                        className={`inline-block w-2 h-2 mt-1 rounded-full flex-shrink-0 ${
                                          evidence.sentiment === 'positive' ? 'bg-green-500' : 
                                          evidence.sentiment === 'negative' ? 'bg-red-500' : 'bg-gray-400'
                                        }`}
                                      ></span>
                                      <span className="text-gray-800">{evidence.text}</span>
                                    </div>
                                  </div>
                                ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : (
            <div className="h-full flex items-center justify-center">
              <div className="text-center">
                <div className="text-gray-400 mb-2">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="text-gray-500 text-lg font-medium">No data available</h3>
                <p className="text-gray-400 text-sm mt-1">Select an entity type to see analysis</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default EntityAnalysis;